import React, { useState } from "react";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";

const toAlignClass = (a) => {
  const v = String(a || "").toLowerCase().trim();
  if (v === "center") return "text-center";
  if (v === "right") return "text-right";
  if (v === "left") return "text-left";
  return "";
};

const buildBg = (style, defaultClass) => {
  const mode = style?.bgMode || "default";

  if (mode === "solid" && style?.bgColor) {
    return { style: { backgroundColor: style.bgColor }, className: "" };
  }

  if (mode === "gradient") {
    const from = (style?.bgGradientFrom || "").trim();
    const via = (style?.bgGradientVia || "").trim();
    const to = (style?.bgGradientTo || "").trim();
    const angleNum = Number(style?.bgGradientAngle);
    const angle = Number.isFinite(angleNum) ? angleNum : 180;

    if (from && to) {
      const stops = via ? `${from}, ${via}, ${to}` : `${from}, ${to}`;
      return {
        style: { backgroundImage: `linear-gradient(${angle}deg, ${stops})` },
        className: "",
      };
    }
  }

  return { style: undefined, className: defaultClass };
};

const TestimonialsSection2 = ({ config, style }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const isRTL = (config?.direction || "ltr") === "rtl";

  const align = (style?.textAlign || "").trim();
  const hasAlign = align.length > 0;

  const headerAlignClass = hasAlign ? toAlignClass(align) : "text-center";
  const cardAlignClass = hasAlign ? toAlignClass(align) : "";

  const iconAlignClass =
    !hasAlign || align === "center"
      ? "mx-auto"
      : align === "right"
      ? "ms-auto"
      : "me-auto";

  const testimonials = Array.isArray(config?.testimonials) && config.testimonials.length
    ? config.testimonials
    : [];

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const bg = buildBg(style, "bg-gradient-to-b from-gray-50 to-white");

  if (!testimonials.length) return null;

  return (
    <section
      dir={config?.direction || "ltr"}
      style={bg.style}
      className={`py-24 px-4 ${bg.className}`}
    >
      <div className="container mx-auto max-w-6xl">
        <div className={`${headerAlignClass} mb-16`}>
          <Quote
            style={{ color: style?.testimonialsQuoteIconColor || undefined }}
            className={`w-12 h-12 text-blue-400 ${iconAlignClass} mb-6`}
          />
          <h2
            style={{ color: style?.titleColor || undefined }}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
          >
            {config?.title || "What Our Customers Say"}
          </h2>
          <p
            style={{ color: style?.subtitleColor || undefined }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            {config?.subtitle || "Join thousands of businesses transforming their operations"}
          </p>
        </div>

        <div className="relative">
          <div className="max-w-4xl mx-auto">
            <div
              style={{
                backgroundColor: style?.testimonialsMainCardBg || undefined,
                borderColor: style?.testimonialsMainCardBorder || undefined,
              }}
              className={`bg-white rounded-3xl shadow-2xl p-8 md:p-12 border ${cardAlignClass}`}
            >
              <div className="flex items-center mb-8">
                <div
                  style={{
                    backgroundColor: style?.testimonialsAvatarBg || undefined,
                    color: style?.testimonialsAvatarText || undefined,
                  }}
                  className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold me-6"
                >
                  {testimonials[currentIndex].avatar}
                </div>
                <div>
                  <h3
                    style={{ color: style?.testimonialsNameColor || undefined }}
                    className="text-2xl font-bold text-gray-900"
                  >
                    {testimonials[currentIndex].name}
                  </h3>
                  <p
                    style={{ color: style?.testimonialsRoleColor || undefined }}
                    className="text-blue-600 font-medium"
                  >
                    {testimonials[currentIndex].role}
                  </p>
                  <p
                    style={{ color: style?.testimonialsCompanyColor || undefined }}
                    className="text-gray-500"
                  >
                    {testimonials[currentIndex].company}
                  </p>
                </div>
              </div>

              <p
                style={{ color: style?.testimonialsContentColor || undefined }}
                className="text-2xl md:text-3xl text-gray-800 italic mb-12 leading-relaxed"
              >
                "{testimonials[currentIndex].content}"
              </p>

              <div className="flex justify-between items-center">
                <div className="flex gap-2">
                  {testimonials.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentIndex(index)}
                      style={{
                        backgroundColor:
                          index === currentIndex
                            ? style?.testimonialsDotActive || undefined
                            : style?.testimonialsDotInactive || undefined,
                      }}
                      className={`w-3 h-3 rounded-full ${
                        index === currentIndex ? "bg-blue-600" : "bg-gray-300"
                      }`}
                    />
                  ))}
                </div>

                <div className="flex gap-4">
                  <button
                    onClick={prevTestimonial}
                    style={{
                      backgroundColor: style?.testimonialsNavBtnBg || undefined,
                      borderColor: style?.testimonialsNavBtnBorder || undefined,
                    }}
                    className="w-12 h-12 rounded-full border-2 border-gray-300 hover:border-blue-600 flex items-center justify-center hover:bg-blue-50 transition-colors"
                  >
                    {isRTL ? (
                      <ChevronRight
                        style={{ color: style?.testimonialsNavBtnIcon || undefined }}
                        className="w-6 h-6 text-gray-600"
                      />
                    ) : (
                      <ChevronLeft
                        style={{ color: style?.testimonialsNavBtnIcon || undefined }}
                        className="w-6 h-6 text-gray-600"
                      />
                    )}
                  </button>

                  <button
                    onClick={nextTestimonial}
                    style={{
                      backgroundColor: style?.testimonialsNavBtnBg || undefined,
                      borderColor: style?.testimonialsNavBtnBorder || undefined,
                    }}
                    className="w-12 h-12 rounded-full border-2 border-gray-300 hover:border-blue-600 flex items-center justify-center hover:bg-blue-50 transition-colors"
                  >
                    {isRTL ? (
                      <ChevronLeft
                        style={{ color: style?.testimonialsNavBtnIcon || undefined }}
                        className="w-6 h-6 text-gray-600"
                      />
                    ) : (
                      <ChevronRight
                        style={{ color: style?.testimonialsNavBtnIcon || undefined }}
                        className="w-6 h-6 text-gray-600"
                      />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                onClick={() => setCurrentIndex(index)}
                style={{
                  backgroundColor:
                    index === currentIndex
                      ? style?.testimonialsPreviewActiveBg || undefined
                      : style?.testimonialsPreviewCardBg || undefined,
                  borderColor:
                    index === currentIndex
                      ? style?.testimonialsPreviewActiveBorder || undefined
                      : style?.testimonialsPreviewCardBorder || undefined,
                }}
                className={`p-6 rounded-2xl cursor-pointer transition-all duration-300 border ${
                  index === currentIndex
                    ? "bg-blue-50 border-2 border-blue-200"
                    : "bg-white border border-gray-200 hover:border-blue-300"
                } ${cardAlignClass}`}
              >
                <div className="flex items-center mb-4">
                  <div
                    style={{
                      backgroundColor: style?.testimonialsAvatarBg || undefined,
                      color: style?.testimonialsAvatarText || undefined,
                    }}
                    className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center text-blue-600 font-bold text-lg me-4"
                  >
                    {testimonial.avatar}
                  </div>
                  <div>
                    <h4
                      style={{ color: style?.testimonialsNameColor || undefined }}
                      className="font-bold text-gray-900"
                    >
                      {testimonial.name}
                    </h4>
                    <p
                      style={{ color: style?.testimonialsRoleColor || undefined }}
                      className="text-sm text-gray-600"
                    >
                      {testimonial.role}
                    </p>
                  </div>
                </div>
                <p
                  style={{ color: style?.testimonialsContentColor || undefined }}
                  className="text-gray-700 line-clamp-3"
                >
                  "{testimonial.content}"
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection2;